import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { t } from "i18next";

import {
  readSessionToken,
  redirectToPortalLogin,
  refreshSessionToken,
  resolveRequestToken,
  SessionExpiredError,
} from "../../services/auth";
import { MutateUserBody } from "./portail/UserService";

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

export abstract class BaseService {
  protected readonly api: AxiosInstance;
  protected readonly pathname: string;

  private URL_API: string;

  constructor({ pathname, apiUrl }: { pathname: string; apiUrl?: string }) {
    this.pathname = pathname;
    this.URL_API = apiUrl || process.env.NEXT_PUBLIC_FLUX_API_URL || "";

    this.api = this.createAxiosInstance();
    this.setupInterceptors();
  }

  private createAxiosInstance(): AxiosInstance {
    return axios.create({
      baseURL: `${this.URL_API}${this.pathname}`,
      timeout: 90000,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
        "Access-Control-Allow-Origin": "*",
      },
    });
  }

  private setupInterceptors(): void {
    this.api.interceptors.request.use(
      this.requestInterceptor.bind(this),
      this.requestErrorInterceptor.bind(this),
    );

    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      this.responseErrorInterceptor.bind(this),
    );
  }

  private endSession(message: string): never {
    redirectToPortalLogin();
    throw new SessionExpiredError(message);
  }

  private async requestInterceptor(
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> {
    let token: string | undefined;

    try {
      token = await resolveRequestToken();
    } catch {
      this.endSession("Session expirée, reconnexion au portail requise");
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  }

  private requestErrorInterceptor(error: AxiosError) {
    return Promise.reject(error);
  }

  private async responseErrorInterceptor(error: AxiosError): Promise<never> {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (
      !originalRequest ||
      originalRequest._retry ||
      error.response?.status !== 401
    ) {
      return Promise.reject(error);
    }

    const staleToken = readSessionToken();
    if (!staleToken) {
      this.endSession("Authentification requise");
    }

    originalRequest._retry = true;

    let newToken: string;
    try {
      newToken = await refreshSessionToken(staleToken);
    } catch {
      this.endSession("Session expirée, reconnexion au portail requise");
    }

    originalRequest.headers.Authorization = `Bearer ${newToken}`;

    return this.api(originalRequest);
  }

  protected async request<R>(config: AxiosRequestConfig): Promise<R> {
    try {
      const response: AxiosResponse<R> = await this.api(config);

      return response.data;
    } catch (error) {
      let message = t("notifications.error.default");

      if (axios.isAxiosError(error)) {
        const data = error.response?.data;
        message = data?.message ?? data?.details ?? message;
      }

      throw new Error(message);
    }
  }

  async searchOrion(search?: Record<string, unknown>): Promise<Response[]> {
    const response = await this.request<{ data: Response[] }>({
      method: "POST",
      url: "/search",
      ...(search && { data: search }),
    });

    return response.data;
  }

  public async mutate(
    mutate: MutateUserBody[],
  ): Promise<{ created: string; updated: string }> {
    const response = await this.api.post(
      `${this.URL_API}/${this.pathname}/mutate`,
      {
        mutate,
      },
    );

    return {
      created: response.data.created[0] ?? "",
      updated: response.data.updated[0] ?? "",
    };
  }
}

export default BaseService;
